import { Fragment, useState } from "react";
import clsx from "clsx";
import { computeHoursFromTimeRange, formatHours } from "@/lib/employeeTimes";
import { AngebotPositionSelect } from "@/pages/admin/zeiterfassung/AngebotPositionSelect";
import {
  findDayEntryGaps,
  rowHasContent,
} from "@/pages/admin/zeiterfassung/dayEntryTimeUtils";
import { ProjectAuftragAutocomplete } from "@/pages/admin/zeiterfassung/ProjectAuftragAutocomplete";
import type { DayEntryRow } from "@/pages/admin/zeiterfassung/DayEntryTable";

interface DayEntryMobileCardsProps {
  rows: DayEntryRow[];
  projectRequired: boolean;
  /** Homeoffice-Schalter nur für Büro (Nicht-Monteure). */
  homeofficeAllowed?: boolean;
  /** Desktop: Projekt/Position oben, Zeiten darunter in einer Zeile. */
  dense?: boolean;
  onUpdateRow: (clientKey: string, patch: Partial<DayEntryRow>) => void;
  onRemoveRow: (clientKey: string) => void;
  onToggleRowKind: (clientKey: string) => void;
  onRowBlur: (clientKey: string) => void;
  onDurationBlur: (clientKey: string) => void;
  onInsertInGap: (afterClientKey: string, gap: { timeFrom: string; timeTo: string }) => void;
  forUserLogin?: string | null;
}

/**
 * Karten-Ansicht der Tageserfassung (Mobile gestapelt, Desktop zweizeilig).
 */
export function DayEntryMobileCards({
  rows,
  projectRequired,
  homeofficeAllowed = false,
  dense = false,
  onUpdateRow,
  onRemoveRow,
  onToggleRowKind,
  onRowBlur,
  onDurationBlur,
  onInsertInGap,
  forUserLogin,
}: DayEntryMobileCardsProps) {
  const [commentOpenKeys, setCommentOpenKeys] = useState<Set<string>>(new Set());
  const gapsByAfterKey = new Map(
    findDayEntryGaps(rows).map((gap) => [gap.afterClientKey, gap]),
  );

  const openComment = (clientKey: string) => {
    setCommentOpenKeys((prev) => {
      const next = new Set(prev);
      next.add(clientKey);
      return next;
    });
  };

  let workCounter = 0;

  return (
    <div className="min-w-0 space-y-3">
      {rows.map((row) => {
        const isBreak = row.rowKind === "break";
        const isNew = !rowHasContent(row);
        const locked = Boolean(row.readOnly);
        const projectLocked = Boolean(row.projectLocked);
        const missingProject =
          !isBreak && projectRequired && rowHasContent(row) && !row.project?.value.id;
        const missingPreisart =
          !isBreak &&
          Boolean(row.positionRequired) &&
          rowHasContent(row) &&
          row.positionId == null;
        const hours =
          !isBreak && row.timeFrom && row.timeTo
            ? computeHoursFromTimeRange(row.timeFrom, row.timeTo)
            : 0;
        if (!isBreak && !isNew) workCounter += 1;

        const title = isBreak
          ? "Pause"
          : isNew
            ? "Neuer Eintrag"
            : `Arbeitszeit ${workCounter}`;
        const showComment =
          Boolean(row.comment) || commentOpenKeys.has(row.clientKey) || dense;
        const gap = gapsByAfterKey.get(row.clientKey);

        return (
          <Fragment key={row.clientKey}>
          <div
            className={clsx(
              "min-w-0 overflow-hidden rounded-ios border bg-card shadow-ios",
              dense ? "p-3.5" : "p-4",
              isBreak && "border-separator bg-fill-tertiary/40",
              !isBreak && isNew && "border-dashed border-separator",
              !isBreak && !isNew && "border-separator",
              projectLocked && "opacity-90",
              missingProject &&
                "border-status-orange bg-[color-mix(in_srgb,var(--eh-orange)_6%,transparent)]",
            )}
          >
            <div className={clsx("flex items-center justify-between gap-2", dense ? "mb-2.5" : "mb-3")}>
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <span
                  className={clsx(
                    "inline-flex rounded-full px-2.5 py-1 text-xs font-semibold",
                    isBreak
                      ? "bg-fill-tertiary text-label-secondary"
                      : isNew
                        ? "bg-fill-tertiary text-label-tertiary"
                        : "bg-accent/10 text-accent",
                  )}
                >
                  {title}
                </span>
                {projectLocked && (
                  <span
                    className="inline-flex rounded-full bg-fill-tertiary px-2.5 py-1 text-xs font-medium text-label-secondary"
                    title="Projekt ist fertiggestellt – Zeiten sind gesperrt"
                  >
                    Gesperrt
                  </span>
                )}
                {!locked && (rowHasContent(row) || isBreak) && (
                  <button
                    type="button"
                    className="rounded-full border border-separator px-2.5 py-1 text-xs font-medium text-label-secondary transition hover:bg-fill-tertiary active:bg-fill-tertiary"
                    onClick={() => onToggleRowKind(row.clientKey)}
                  >
                    {isBreak ? "→ Arbeitszeit" : "→ Pause"}
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                {hours > 0 && (
                  <span className="font-mono text-sm font-semibold tabular-nums text-label">
                    {formatHours(hours)}
                  </span>
                )}
                {rowHasContent(row) && !locked && (
                  <button
                    type="button"
                    className="-m-1 flex h-10 w-10 items-center justify-center rounded-full text-label-tertiary transition hover:bg-fill-tertiary active:bg-fill-tertiary"
                    aria-label="Eintrag entfernen"
                    onClick={() => onRemoveRow(row.clientKey)}
                  >
                    <span className="text-[1.4rem] leading-none" aria-hidden>
                      ×
                    </span>
                  </button>
                )}
              </div>
            </div>

            {!isBreak && (
              <div
                className={clsx(
                  dense ? "mb-2.5 grid grid-cols-1 gap-2 sm:grid-cols-2" : "mb-3 space-y-3",
                )}
              >
                <ProjectAuftragAutocomplete
                  key={`${row.clientKey}-${row.project?.value.id ?? "none"}`}
                  label={projectRequired ? "Projekt *" : "Projekt (optional)"}
                  value={row.project}
                  onChange={(next) =>
                    onUpdateRow(row.clientKey, {
                      project: next,
                      positionId: null,
                      positionRequired: false,
                    })
                  }
                  required={projectRequired}
                  placeholder={projectRequired ? "Projekt wählen *" : "Projekt wählen"}
                  error={missingProject ? "Bitte Projekt wählen." : undefined}
                  disabled={locked}
                  forUserLogin={forUserLogin}
                />
                {row.project?.value.id ? (
                  <AngebotPositionSelect
                    auftragId={row.project.value.id}
                    value={row.positionId}
                    onChange={(positionId) => onUpdateRow(row.clientKey, { positionId })}
                    onRequiredChange={(positionRequired) => {
                      if (Boolean(row.positionRequired) === positionRequired) return;
                      onUpdateRow(row.clientKey, { positionRequired });
                    }}
                    error={
                      missingPreisart ? "Bitte Nachweis- oder Festpreisposition wählen." : undefined
                    }
                    disabled={locked}
                  />
                ) : dense ? (
                  <div className="hidden sm:block" aria-hidden />
                ) : null}
              </div>
            )}

            <div
              className={clsx(
                "ze-mobile-time-fields",
                dense
                  ? "grid grid-cols-2 gap-2 md:grid-cols-4"
                  : "space-y-3",
              )}
            >
              {!dense && (
                <div className="grid grid-cols-2 gap-3">
                  <label className="min-w-0 space-y-1">
                    <span className="text-xs font-medium text-label-secondary">Von</span>
                    <input
                      type="time"
                      className="eh-input eh-ze-time-input h-12 w-full min-w-0 tabular-nums"
                      value={row.timeFrom}
                      disabled={locked}
                      onChange={(e) => onUpdateRow(row.clientKey, { timeFrom: e.target.value })}
                      onBlur={() => onRowBlur(row.clientKey)}
                    />
                  </label>
                  <label className="min-w-0 space-y-1">
                    <span className="text-xs font-medium text-label-secondary">Bis</span>
                    <input
                      type="time"
                      className="eh-input eh-ze-time-input h-12 w-full min-w-0 tabular-nums"
                      value={row.timeTo}
                      disabled={locked}
                      onChange={(e) => onUpdateRow(row.clientKey, { timeTo: e.target.value })}
                      onBlur={() => onRowBlur(row.clientKey)}
                    />
                  </label>
                </div>
              )}
              {dense && (
                <>
                  <label className="min-w-0 space-y-1">
                    <span className="text-xs font-medium text-label-secondary">Von</span>
                    <input
                      type="time"
                      className="eh-input eh-ze-time-input h-11 w-full min-w-0 tabular-nums"
                      value={row.timeFrom}
                      disabled={locked}
                      onChange={(e) => onUpdateRow(row.clientKey, { timeFrom: e.target.value })}
                      onBlur={() => onRowBlur(row.clientKey)}
                    />
                  </label>
                  <label className="min-w-0 space-y-1">
                    <span className="text-xs font-medium text-label-secondary">Dauer</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="eh-input h-11 w-full min-w-0 tabular-nums"
                      placeholder="1,5 / 1:30"
                      title="Stunden (1,5) oder Stunden:Minuten (1:30)"
                      value={row.durationInput}
                      disabled={locked}
                      onChange={(e) =>
                        onUpdateRow(row.clientKey, { durationInput: e.target.value })
                      }
                      onBlur={() => {
                        onDurationBlur(row.clientKey);
                        onRowBlur(row.clientKey);
                      }}
                    />
                  </label>
                  <label className="min-w-0 space-y-1">
                    <span className="text-xs font-medium text-label-secondary">Bis</span>
                    <input
                      type="time"
                      className="eh-input eh-ze-time-input h-11 w-full min-w-0 tabular-nums"
                      value={row.timeTo}
                      disabled={locked}
                      onChange={(e) => onUpdateRow(row.clientKey, { timeTo: e.target.value })}
                      onBlur={() => onRowBlur(row.clientKey)}
                    />
                  </label>
                </>
              )}
              {!dense && (
                <label className="block min-w-0 space-y-1">
                  <span className="text-xs font-medium text-label-secondary">
                    Dauer (Stunden)
                  </span>
                  <input
                    type="text"
                    inputMode="decimal"
                    className="eh-input h-12 w-full min-w-0 tabular-nums"
                    placeholder="z. B. 1,5 oder 1:30"
                    value={row.durationInput}
                    disabled={locked}
                    onChange={(e) =>
                      onUpdateRow(row.clientKey, { durationInput: e.target.value })
                    }
                    onBlur={() => {
                      onDurationBlur(row.clientKey);
                      onRowBlur(row.clientKey);
                    }}
                  />
                </label>
              )}
              {dense && (
                showComment ? (
                  <label className="min-w-0 space-y-1 col-span-2 md:col-span-1">
                    <span className="text-xs font-medium text-label-secondary">Kommentar</span>
                    <input
                      type="text"
                      className="eh-input h-11 w-full min-w-0"
                      placeholder={isBreak ? "Pause" : "Optional"}
                      value={row.comment}
                      disabled={locked}
                      onChange={(e) => onUpdateRow(row.clientKey, { comment: e.target.value })}
                    />
                  </label>
                ) : null
              )}
            </div>

            {!dense &&
              (showComment ? (
                <label className="mt-3 block space-y-1">
                  <span className="text-xs font-medium text-label-secondary">Kommentar</span>
                  <input
                    type="text"
                    className="eh-input h-12 w-full min-w-0"
                    placeholder={isBreak ? "Pause" : "Optional"}
                    value={row.comment}
                    disabled={locked}
                    onChange={(e) => onUpdateRow(row.clientKey, { comment: e.target.value })}
                  />
                </label>
              ) : (
                <button
                  type="button"
                  className="mt-2 py-1 text-sm font-medium text-label-secondary active:text-accent"
                  onClick={() => openComment(row.clientKey)}
                  disabled={locked}
                >
                  + Kommentar
                </button>
              ))}

            {homeofficeAllowed && !isBreak && (
              <div
                className={clsx(
                  "flex flex-wrap items-center gap-x-2.5 gap-y-1",
                  dense ? "mt-2.5" : "mt-3",
                )}
              >
                <button
                  type="button"
                  role="switch"
                  aria-checked={row.isHomeoffice}
                  disabled={locked}
                  title="Für die Steuererklärung / Homeoffice-Pauschale"
                  onClick={() =>
                    onUpdateRow(row.clientKey, { isHomeoffice: !row.isHomeoffice })
                  }
                  className={clsx(
                    "inline-flex shrink-0 items-center rounded-full border px-3 py-1 text-xs font-semibold transition",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-card",
                    "disabled:cursor-not-allowed disabled:opacity-50",
                    row.isHomeoffice
                      ? "border-accent bg-accent text-label-on-accent shadow-sm"
                      : "border-separator bg-card text-label-secondary hover:border-accent hover:text-accent",
                  )}
                >
                  Homeoffice
                </button>
                <span className="pointer-events-none text-xs text-label-tertiary">
                  Für die Steuererklärung / Homeoffice-Pauschale
                </span>
              </div>
            )}

            {projectLocked && (
              <p className="mt-2 text-xs text-label-secondary">
                Projekt fertiggestellt – diese Zeiten können nicht mehr verändert werden.
              </p>
            )}

            {isNew && !locked && (
              <p className="mt-2 text-xs text-label-tertiary">
                Einfach „Bis“ oder die Dauer eintragen — der Eintrag wird beim Speichern
                übernommen.
              </p>
            )}
          </div>
          {gap ? (
            <div className="flex items-center justify-center gap-2 py-0.5">
              <span className="h-px min-w-6 flex-1 bg-separator" aria-hidden />
              <button
                type="button"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-separator bg-card text-xl font-medium leading-none text-label-secondary shadow-ios transition hover:border-accent hover:text-accent active:bg-fill-tertiary"
                aria-label={`Zeiteintrag ${gap.timeFrom}–${gap.timeTo} einfügen`}
                title={`Eintrag ${gap.timeFrom}–${gap.timeTo} einfügen`}
                onClick={() => onInsertInGap(gap.afterClientKey, gap)}
              >
                <span aria-hidden>+</span>
              </button>
              <span className="h-px min-w-6 flex-1 bg-separator" aria-hidden />
            </div>
          ) : null}
          </Fragment>
        );
      })}
    </div>
  );
}
